interface PacsEntry {
  aet: string;
  found: boolean;
  patientName: string;
  patientID: string;
  includedSeriesCount: number;
  excludedSeriesCount: number;
  instanceCount: number;
  excludedInstanceCount: number;
  error: string | null;
}

interface StudyConsistencyResult {
  studyInstanceUID: string;
  pacs: PacsEntry[];
  allFound: boolean;
  patientNameMatch: boolean;
  patientIDMatch: boolean;
  instanceCountMatch: boolean;
  verified: boolean;
}

/**
 * Calls VerifyStudyAcrossPacs for all given study UIDs in a single batch request.
 * Any HTTP or network failure is treated as "not verified" (fail-closed).
 */
async function fetchConsistencyResults(
  baseURLDicomWebAPI: string,
  studyInstanceUIDs: string[],
  authorizationHeader: string
): Promise<StudyConsistencyResult[]> {
  const queryParams = studyInstanceUIDs.map(uid => `studyUID=${encodeURIComponent(uid)}`).join('&');
  const url = `${baseURLDicomWebAPI}VerifyStudyAcrossPacs?${queryParams}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept: '*/*',
      ...(authorizationHeader ? { Authorization: authorizationHeader } : {}),
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Verifies that every given study is consistent across PACS via the
 * VerifyStudyAcrossPacs API. Resolves to true only if all studies have
 * `verified: true` in the response.
 *
 * @param studyInstanceUIDs - the studies to verify
 * @param userAuthenticationService - used to attach the same bearer token as other authenticated requests
 */
export async function verifyStudiesConsistency(
  studyInstanceUIDs: string[],
  userAuthenticationService: any
): Promise<boolean> {
  const baseURLDicomWebAPI = window.config?.baseURLDicomWebAPI;

  if (!baseURLDicomWebAPI || !studyInstanceUIDs?.length) {
    return true;
  }

  const authorizationHeader = userAuthenticationService?.getAuthorizationHeader()
    ? userAuthenticationService.getAuthorizationHeader().Authorization
    : '';

  try {
    const results = await fetchConsistencyResults(baseURLDicomWebAPI, studyInstanceUIDs, authorizationHeader);

    if (!Array.isArray(results) || results.length === 0) {
      console.error('VerifyStudyAcrossPacs returned unexpected response format');
      return false;
    }

    const unverified = results.filter(r => !r.verified);
    if (unverified.length > 0) {
      unverified.forEach(r => {
        console.warn(`Study not consistent: ${r.studyInstanceUID}`, {
          allFound: r.allFound,
          patientNameMatch: r.patientNameMatch,
          patientIDMatch: r.patientIDMatch,
          instanceCountMatch: r.instanceCountMatch,
        });
      });
    }

    return results.every(r => r.verified === true);
  } catch (error) {
    console.error('VerifyStudyAcrossPacs error:', error);
    return false;
  }
}
